import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import CaptionedValue from '@/common/components/CaptionedValue/CaptionedValue'
import useNumberFormatter from '@/common/hooks/useNumberFormatter'
import { getSizeCaptionKey } from '@/common/utils/SheetUtils/SheetUtils'

const RenderPartSize = ({ part, variant = 'body2', ...props }) => {
    const { t } = useTranslation()
    const partIsImperial = part?.isImperial
    const partHasSheet = Boolean(part?.sheet)
    const profile = part?.sheet?.profile?.toLowerCase() || part?.profile?.toLowerCase()
    const isRotaryPart = part && 'profile' in part && Boolean(part?.profile)

    const { n: metricNumberFormatter } = useNumberFormatter({
        numberOfDecimalPlaces: 2,
    })

    const { n: imperialNumberFormatter } = useNumberFormatter({
        numberOfDecimalPlaces: 4,
    })

    const n = partIsImperial ? imperialNumberFormatter : metricNumberFormatter
    const partUnits = () => (partIsImperial ? 'in' : 'mm')

    const bounds = isRotaryPart
        ? profile === 'circle'
            ? partHasSheet
                ? `${n(part?.sheet?.diameter)} x ${n(part?.rotaryPartLength)}`
                : `${n(part?.diameter)} x ${n(part?.rotaryPartLength)}`
            : partHasSheet
              ? `${n(part?.sheet?.sheetWidth)} x ${n(part?.sheet?.sheetHeight)} x ${n(part?.rotaryPartLength)}`
              : `${n(part?.rotaryProfileWidth)} x ${n(part?.rotaryProfileLength)} x ${n(part?.rotaryPartLength)}`
        : `${n(part.displayBoundBoxWidth)} x ${n(part.displayBoundBoxHeight)}`

    const caption = t(getSizeCaptionKey({ hasLength: isRotaryPart, isCircle: isRotaryPart && profile === 'circle' }))

    return (
        <CaptionedValue
            align={props.textAlign}
            caption={caption}
        >
            <Typography
                sx={{ whiteSpace: 'break-spaces' }}
                variant={variant}
                {...props}
            >{`${bounds} ${partUnits()}`}</Typography>
        </CaptionedValue>
    )
}

RenderPartSize.propTypes = {
    part: PropTypes.object.isRequired,
    textAlign: PropTypes.oneOf(['center', 'left', 'right']),
    variant: PropTypes.string,
}

export default memo(RenderPartSize)
